// Ported verbatim from the design/rev3 prototype (shell.jsx:3-40).
// Typed React components. Each accepts { size? } (default 18); viewBox "0 0 24 24",
// stroke-width 1.7 (a few use 1.8/1.9/2 in the source), stroke="currentColor" fill="none".
//
// This file is a namespace of icon components exported via the single `Icon` const.
// react-refresh can't recognise an object-of-components as a component export, so the
// rule is disabled for this file (Fast Refresh is irrelevant for static SVG icons).
/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react'
import type { IconProps } from './icons.types'

function Svg({
  size = 18,
  strokeWidth = 1.7,
  style,
  className,
  title,
  'aria-label': ariaLabel,
  'data-icon': dataIcon,
  children,
}: IconProps & { strokeWidth?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      role={title || ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel ?? title}
      data-icon={dataIcon}
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  )
}

export const Icon = {
  dash: (p: IconProps) => (
    <Svg {...p}>
      <rect x={3} y={3} width={7} height={9} rx={1.4} />
      <rect x={14} y={3} width={7} height={5} rx={1.4} />
      <rect x={14} y={12} width={7} height={9} rx={1.4} />
      <rect x={3} y={16} width={7} height={5} rx={1.4} />
    </Svg>
  ),
  list: (p: IconProps) => (
    <Svg {...p}>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx={3.5} cy={6} r={1.1} fill="currentColor" stroke="none" />
      <circle cx={3.5} cy={12} r={1.1} fill="currentColor" stroke="none" />
      <circle cx={3.5} cy={18} r={1.1} fill="currentColor" stroke="none" />
    </Svg>
  ),
  server: (p: IconProps) => (
    <Svg {...p}>
      <rect x={3} y={4} width={18} height={7} rx={2} />
      <rect x={3} y={13} width={18} height={7} rx={2} />
      <circle cx={7} cy={7.5} r={1} fill="currentColor" stroke="none" />
      <circle cx={7} cy={16.5} r={1} fill="currentColor" stroke="none" />
    </Svg>
  ),
  building: (p: IconProps) => (
    <Svg {...p}>
      <rect x={4} y={3} width={11} height={18} rx={1.5} />
      <path d="M15 9h5v12h-5M8 7h3M8 11h3M8 15h3" />
    </Svg>
  ),
  bell: (p: IconProps) => (
    <Svg {...p}>
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </Svg>
  ),
  users: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={9} cy={8} r={3.2} />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0M17 7.5a3 3 0 0 1 0 5.8M20.5 20a5 5 0 0 0-4-4.9" />
    </Svg>
  ),
  user: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={12} cy={8} r={3.6} />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </Svg>
  ),
  mail: (p: IconProps) => (
    <Svg {...p}>
      <rect x={3} y={5} width={18} height={14} rx={2} />
      <path d="m3.6 7 8.4 6 8.4-6" />
    </Svg>
  ),
  hash: (p: IconProps) => (
    <Svg {...p}>
      <path d="M9.5 4 7.5 20M16.5 4l-2 16M4 9h16M3 15h16" />
    </Svg>
  ),
  info: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={9} />
      <path d="M12 11.5v4.5" />
      <circle cx={12} cy={7.9} r={1} fill="currentColor" stroke="none" />
    </Svg>
  ),
  sliders: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h7M15 18h5" />
      <circle cx={16} cy={6} r={2} />
      <circle cx={8} cy={12} r={2} />
      <circle cx={13} cy={18} r={2} />
    </Svg>
  ),
  search: (p: IconProps) => (
    <Svg {...p} strokeWidth={1.8}>
      <circle cx={11} cy={11} r={7} />
      <path d="m20 20-3.2-3.2" />
    </Svg>
  ),
  plus: (p: IconProps) => (
    <Svg {...p} strokeWidth={1.9}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  download: (p: IconProps) => (
    <Svg {...p} strokeWidth={1.8}>
      <path d="M12 4v11m0 0 4-4m-4 4-4-4M5 20h14" />
    </Svg>
  ),
  chevron: (p: IconProps) => (
    <Svg {...p} strokeWidth={1.9}>
      <path d="m9 5 7 7-7 7" />
    </Svg>
  ),
  filter: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 5h18l-7 8v6l-4 2v-8z" />
    </Svg>
  ),
  clock: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={8.5} />
      <path d="M12 7.5V12l3 1.8" />
    </Svg>
  ),
  pin: (p: IconProps) => (
    <Svg {...p}>
      <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
      <circle cx={12} cy={10} r={2.4} />
    </Svg>
  ),
  phone: (p: IconProps) => (
    <Svg {...p}>
      <path d="M5 4h3.5l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5V19a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z" />
    </Svg>
  ),
  link: (p: IconProps) => (
    <Svg {...p}>
      <path d="M10 13a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7L11.5 6" />
      <path d="M14 11a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7L12.5 18" />
    </Svg>
  ),
  key: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={8} cy={8} r={4.5} />
      <path d="m11 11 8 8M16 16l2-2M19 19l2-2" />
    </Svg>
  ),
  lock: (p: IconProps) => (
    <Svg {...p}>
      <rect x={4.5} y={10.5} width={15} height={10} rx={2} />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </Svg>
  ),
  eye: (p: IconProps) => (
    <Svg {...p}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx={12} cy={12} r={3} />
    </Svg>
  ),
  note: (p: IconProps) => (
    <Svg {...p}>
      <path d="M5 3h14v14l-5 4H5z" />
      <path d="M14 21v-4h5M9 9h6M9 13h4" />
    </Svg>
  ),
  wrench: (p: IconProps) => (
    <Svg {...p}>
      <path d="M15 6a4 4 0 0 0-5.2 5.2l-6 6L5.8 19l6-6A4 4 0 0 0 17 7.8L14.5 10 12 7.5 14.2 5" />
    </Svg>
  ),
  pencil: (p: IconProps) => (
    <Svg {...p}>
      <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
      <path d="M13.5 6.5l3 3" />
    </Svg>
  ),
  trash: (p: IconProps) => (
    <Svg {...p} data-icon="trash">
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13" />
      <path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
    </Svg>
  ),
  x: (p: IconProps) => (
    <Svg {...p} strokeWidth={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Svg>
  ),
  check: (p: IconProps) => (
    <Svg {...p} strokeWidth={2}>
      <path d="m5 12 5 5L20 6" />
    </Svg>
  ),
  spark: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 17l5-6 4 3 5-7 4 5" />
    </Svg>
  ),
  gear: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={3.2} />
      <path d="M12 2.5v2.2M12 19.3v2.2M21.5 12h-2.2M4.7 12H2.5M18.7 5.3l-1.6 1.6M6.9 17.1l-1.6 1.6M18.7 18.7l-1.6-1.6M6.9 6.9 5.3 5.3" />
    </Svg>
  ),
  refresh: (p: IconProps) => (
    <Svg {...p}>
      <path d="M20 11a8 8 0 0 0-14-4.5L4 8M4 4v4h4M4 13a8 8 0 0 0 14 4.5L20 16M20 20v-4h-4" />
    </Svg>
  ),
  warn: (p: IconProps) => (
    <Svg {...p} strokeWidth={1.8}>
      <path d="M12 3.5 21.5 20H2.5L12 3.5Z" />
      <path d="M12 10v4.2" />
      <circle cx={12} cy={17.4} r={1} fill="currentColor" stroke="none" />
    </Svg>
  ),
  tag: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 11.5V5a2 2 0 0 1 2-2h6.5a2 2 0 0 1 1.4.6l7.5 7.5a2 2 0 0 1 0 2.8l-6.6 6.6a2 2 0 0 1-2.8 0L3.6 12.9A2 2 0 0 1 3 11.5Z" />
      <circle cx={7.5} cy={7.5} r={1.4} fill="currentColor" stroke="none" />
    </Svg>
  ),
  calendar: (p: IconProps) => (
    <Svg {...p}>
      <rect x={3.5} y={5} width={17} height={16} rx={2} />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </Svg>
  ),
  folder: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </Svg>
  ),
  block: (p: IconProps) => (
    <Svg {...p} data-icon="block">
      <circle cx={12} cy={12} r={9} />
      <path d="M5.6 5.6 18.4 18.4" />
    </Svg>
  ),
  // Soft-Toggle (#92): Pause = II, Start = Play ▶. NICHT für hartes Löschen/Block.
  pause: (p: IconProps) => (
    <Svg {...p} data-icon="pause">
      <path d="M9 5v14M15 5v14" />
    </Svg>
  ),
  play: (p: IconProps) => (
    <Svg {...p} data-icon="play">
      <path d="M8 5.5v13l11-6.5z" />
    </Svg>
  ),
  stop: (p: IconProps) => (
    <Svg {...p} data-icon="stop">
      <path d="M7 7h10v10H7z" />
    </Svg>
  ),
  xcircle: (p: IconProps) => (
    <Svg {...p}>
      <circle cx={12} cy={12} r={9} />
      <path d="M15 9l-6 6M9 9l6 6" />
    </Svg>
  ),
  ticket: (p: IconProps) => (
    <Svg {...p}>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 8 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-8Z" />
      <path d="M14 6v12" strokeDasharray="2 2" />
    </Svg>
  ),
  teams: (p: IconProps) => (
    <Svg {...p}>
      <rect x={3} y={6} width={12} height={12} rx={2.5} />
      <path d="M6.2 9.5h5.6M9 9.5V15" />
      <circle cx={18} cy={7.5} r={2.2} />
      <path d="M15.5 18v-3.6a2.4 2.4 0 0 1 2.4-2.4h.6a2.5 2.5 0 0 1 2.5 2.5V17a1 1 0 0 1-1 1Z" />
    </Svg>
  ),
  shield: (p: IconProps) => (
    <Svg {...p}>
      <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z" />
    </Svg>
  ),
}
